import type { FC } from "react";
import { isWindowDefined } from "@huub-web/utils";
import {
  AuthProvider as BaseAuthProvider,
  type AuthProviderProps as AuthProviderBaseProps,
} from "@huub-web/backend";

import { AUTH_MODE } from "../../config/authConfig";

export type AuthProviderProps = Pick<
  AuthProviderBaseProps,
  | "loginURL"
  | "logoutEndpoint"
  | "refreshEndpoint"
  | "authCookieDomain"
  | "authCookieName"
  | "children"
>;

/**
 * Get the login URL based on the auth mode.
 * When AUTH_MODE=internal, uses in-app /auth/login route.
 * When AUTH_MODE=external, uses the provided external login URL.
 */
const getAuthLoginURL = (externalLoginURL: string): string => {
  if (AUTH_MODE === "internal") {
    return "/auth/login";
  }
  return externalLoginURL;
};

/**
 * AuthProvider component that wraps the base AuthProvider and passes the required props.
 * Supports two auth modes:
 * - "external": Redirects to marketing site for auth (default)
 * - "internal": Uses in-app auth pages (`/auth/login`, `/auth/callback`) for whitelabel deployments
 */
export const AuthProvider: FC<AuthProviderProps> = ({
  loginURL,
  children,
  ...props
}) => {
  const resolvedLoginURL = getAuthLoginURL(loginURL);

  return (
    <BaseAuthProvider
      {...props}
      loginURL={resolvedLoginURL}
      onUnauthorized={(type) => {
        if (!isWindowDefined()) {
          return;
        }

        const loginTarget = AUTH_MODE === "internal" ? "/auth/login" : loginURL;

        if (type === "invalid") {
          window.location.href = `${loginTarget}?error=session_expired`;
        } else {
          window.location.href = loginTarget;
        }
      }}
      onProviderMismatch={(error) => {
        console.warn("Provider mismatch detected:", error.message);
      }}
      onLogout={(failure) => {
        if (!isWindowDefined()) {
          return;
        }

        if (AUTH_MODE === "internal") {
          const url = failure
            ? "/auth/login?error=logout_failed"
            : "/auth/login?logged_out=true";
          window.location.href = url;
        } else {
          const url = failure
            ? `${loginURL}?error=logout_failed`
            : `${loginURL}?logged_out=true`;
          window.location.href = url;
        }
      }}
    >
      {children}
    </BaseAuthProvider>
  );
};
